import { Button } from 'antd';
import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../shared/permissions';
import { useGetModulesOptionsQuery } from '../../../api/queries/getModulesOptions';
import { type ModuleFilteringRule, ModuleType } from '../../../api/types';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import NoItemResult from '../../../components/results/NoItemResult';
import hasPermissions from '../../../utilities/hasPermissions';
import useGoTo from '../../../utilities/useGoTo';
import DashboardContent from './DashboardContent';
import DashboardContext, {
    type DashboardContextState,
    type DashboardContextValue,
    type ViewIDs,
} from './DashboardContext';

const initialState: DashboardContextState = {
    displayCurrentMonth: true,
    displayLastMonth: true,
    visible: {
        statistics: true,
        applicationChart: true,
        popularApplicationVariantChart: true,
        popularLeadEventChart: true,
        popularLeadVariantChart: true,
        leadChart: true,
        depositPaymentsPerDay: true,
        topSalesManWithApplication: true,
        topSalesManWithLead: true,
        topSalesManWithLoan: true,
        latestApplicationTable: true,
    },
};

type DashboardAction =
    | { type: 'setDisplayCurrentMonth'; display: boolean }
    | { type: 'setDisplayLastMonth'; display: boolean }
    | { type: 'setVisible'; updates: { [viewID in ViewIDs]?: boolean } };

const reducer = (state: DashboardContextState, action: DashboardAction): DashboardContextState => {
    switch (action.type) {
        case 'setDisplayCurrentMonth':
            return {
                ...state,
                displayCurrentMonth: action.display,
            };

        case 'setDisplayLastMonth':
            return {
                ...state,
                displayLastMonth: action.display,
            };

        case 'setVisible':
            return {
                ...state,
                visible: {
                    ...state.visible,
                    ...action.updates,
                },
            };

        default:
            return state;
    }
};

const DashboardLoader = () => {
    const { t } = useTranslation(['dashboard', 'moduleList']);
    const { permissions: accountPermissions } = useAccount();

    const [state, dispatch] = useReducer(reducer, initialState);

    const actions = useMemo<Omit<DashboardContextValue, 'state'>>(
        () => ({
            setDisplayCurrentMonth: (display: boolean) => dispatch({ type: 'setDisplayCurrentMonth', display }),
            setDisplayLastMonth: (display: boolean) => dispatch({ type: 'setDisplayLastMonth', display }),
            setVisible: (updates: { [viewID in ViewIDs]?: boolean }) => dispatch({ type: 'setVisible', updates }),
        }),
        [dispatch]
    );

    const context = useMemo(() => ({ ...actions, state }), [state, actions]);

    const company = useCompany();

    const filter = useMemo(
        (): ModuleFilteringRule => ({
            moduleTypes: [
                ModuleType.EventApplicationModule,
                ModuleType.StandardApplicationModule,
                ModuleType.AdyenPaymentModule,
                ModuleType.PorschePaymentModule,
                ModuleType.FinderApplicationPublicModule,
                ModuleType.FinderApplicationPrivateModule,
                ModuleType.SdmModule,
            ],
            companyId: company.id,
        }),
        [company]
    );

    const { data } = useGetModulesOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { ...filter, companyId: company.id } },
    });

    const goToNewModulePage = useGoTo('/admin/system/modules/add');

    const items = data?.modules?.items || [];

    // reset state on company changed
    const currentCompany = useRef(company.id);
    useEffect(() => {
        if (currentCompany.current !== company.id) {
            currentCompany.current = company.id;
            dispatch({ type: 'setVisible', updates: initialState.visible });
        }
    }, [company.id, dispatch]);

    if (items.length === 0) {
        return (
            <NoItemResult
                extra={
                    hasPermissions(accountPermissions, [permissionKind.createModule]) ? (
                        <Button onClick={goToNewModulePage} type="primary">
                            {t('moduleList:noData.action')}
                        </Button>
                    ) : null
                }
                subTitle={t('moduleList:noData.message')}
            />
        );
    }

    return (
        <DashboardContext.Provider value={context}>
            <DashboardContent items={items} />
        </DashboardContext.Provider>
    );
};

export default DashboardLoader;
